export interface PdfSliceRange {
  top: number;
  bottom: number;
  keepWithNext?: boolean;
}

export interface PdfPageSlice {
  start: number;
  end: number;
}

export const QUOTE_PDF_UNBREAKABLE_SELECTORS: Array<{
  selector: string;
  keepWithNext?: boolean;
}> = [
  { selector: '.header' },
  { selector: '.info-table' },
  { selector: '.summary-section' },
  { selector: '.detail-section > .section-title', keepWithNext: true },
  { selector: '.detail-section .detail-table thead tr', keepWithNext: true },
  { selector: '.detail-section .detail-table tbody tr' },
  { selector: '.summary-table' },
  { selector: '.terms-block' },
  { selector: '.sign-block' },
];

const MIN_SLICE_HEIGHT = 8;

/**
 * 依不可切斷區塊（如表格列）計算 PDF 分頁裁切點，避免列被切成兩半。
 * 座標單位需一致（例如皆為 canvas pixel）。
 */
export function computePageSlices(
  contentHeight: number,
  pageHeight: number,
  ranges: PdfSliceRange[]
): PdfPageSlice[] {
  if (contentHeight <= 0) {
    return [];
  }

  const safePageHeight =
    Number.isFinite(pageHeight) && pageHeight > MIN_SLICE_HEIGHT
      ? pageHeight
      : Math.min(contentHeight, 1200);

  const sorted = [...ranges]
    .filter((range) => Number.isFinite(range.top) && Number.isFinite(range.bottom) && range.bottom > range.top)
    .sort((a, b) => a.top - b.top || a.bottom - b.bottom);

  const slices: PdfPageSlice[] = [];
  let start = 0;

  while (start < contentHeight - 0.5) {
    const maxEnd = Math.min(start + safePageHeight, contentHeight);
    if (maxEnd >= contentHeight - 0.5) {
      slices.push({ start, end: contentHeight });
      break;
    }

    let end = findSafeBreak(start, maxEnd, sorted);
    if (end <= start + MIN_SLICE_HEIGHT) {
      end = maxEnd;
    }
    if (end <= start) {
      end = maxEnd;
    }

    slices.push({ start, end });
    start = end;
  }

  return slices;
}

function findSafeBreak(
  start: number,
  maxEnd: number,
  ranges: PdfSliceRange[]
): number {
  let end = maxEnd;

  const overlapping = ranges.find((range) => range.top < end && range.bottom > end);
  if (overlapping && overlapping.top > start) {
    end = overlapping.top;
  }

  let moved = true;
  while (moved) {
    moved = false;
    const keepers = ranges.filter(
      (range) =>
        range.keepWithNext &&
        range.top >= start &&
        range.bottom <= end + 0.5 &&
        range.top < end
    );
    const lastKeeper = keepers[keepers.length - 1];
    if (!lastKeeper || lastKeeper.top <= start) {
      break;
    }

    const hasFollowerOnPage = ranges.some(
      (range) =>
        range !== lastKeeper &&
        range.top >= lastKeeper.bottom - 0.5 &&
        range.top < end &&
        range.bottom <= end + 0.5
    );
    if (!hasFollowerOnPage) {
      const pulled = lastKeeper.top;
      if (pulled > start + MIN_SLICE_HEIGHT && pulled < end) {
        end = pulled;
        moved = true;

        const again = ranges.find((range) => range.top < end && range.bottom > end);
        if (again && again.top > start) {
          end = again.top;
        }
      }
    }
  }

  return end;
}

export function collectElementRanges(
  root: HTMLElement,
  items: Array<{ selector: string; keepWithNext?: boolean }>,
  scaleY: number
): PdfSliceRange[] {
  const rootRect = root.getBoundingClientRect();
  const ranges: PdfSliceRange[] = [];

  items.forEach((item) => {
    root.querySelectorAll(item.selector).forEach((el) => {
      const rect = el.getBoundingClientRect();
      const top = (rect.top - rootRect.top + root.scrollTop) * scaleY;
      const bottom = (rect.bottom - rootRect.top + root.scrollTop) * scaleY;
      if (bottom > top) {
        ranges.push({
          top,
          bottom,
          keepWithNext: item.keepWithNext,
        });
      }
    });
  });

  return ranges;
}
